const TAG_NAME = 'tfp-newsletter-popup';
const CONSENT_EVENT = 'visitorConsentCollected';
const STORAGE_PREFIX = 'tfp-newsletter-engagement:';

class TfpNewsletterPopup extends HTMLElement {
  #abortController;
  #autoTimer;
  #sideTimer;
  #mutationObserver;
  #drawer;
  #payload;
  #slot;
  #payloadHost;
  #sideTab;
  #sideTabWrap;
  #suppressedInMemory = false;
  #tabSuppressedInMemory = false;
  #consentState = 'unknown';
  #autoDelayMet = false;
  #sideDelayMet = false;
  #autoScrolled = false;
  #sideScrolled = false;
  #autoVisibleMs = 0;
  #sideVisibleMs = 0;
  #visibleSince = 0;
  #pendingOpen = null;
  #opened = false;
  #connectionVersion = 0;
  #sideTabReturnFocus = null;
  #privacyLoadPending = false;
  #privacyReadinessArmed = false;
  #feedbackTarget = null;
  #feedbackPresented = false;
  #feedbackPresentationPending = false;

  connectedCallback() {
    this.#connectionVersion += 1;
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#privacyLoadPending = false;
    this.#privacyReadinessArmed = false;
    this.#drawer = this.querySelector('theme-drawer');
    this.#payload = this.querySelector('[data-tfp-newsletter-payload]');
    this.#slot = this.querySelector('[data-tfp-newsletter-slot]');
    this.#payloadHost = this.querySelector('[data-tfp-newsletter-host]');
    this.#sideTab = this.querySelector('.tfp-newsletter__side-tab');
    this.#sideTabWrap = this.querySelector('[data-tfp-newsletter-tab]');

    if (!(this.#drawer instanceof HTMLElement) || !this.#payload || !this.#slot) return;

    this.addEventListener('click', this.#handleClick, {
      capture: true,
      signal: this.#abortController.signal,
    });
    this.#drawer.addEventListener('theme-drawer:close', this.#handleDrawerClose, {
      signal: this.#abortController.signal,
    });
    this.#drawer.querySelector('dialog')?.addEventListener('close', () => {
      this.#restorePayload();
      this.#armTimers();
      this.#showSideTabIfEligible();
      this.#restoreSideTabFocus();
    }, { signal: this.#abortController.signal });
    document.addEventListener(CONSENT_EVENT, this.#handleConsentEvent, {
      signal: this.#abortController.signal,
    });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, {
      signal: this.#abortController.signal,
    });
    document.addEventListener('theme-drawer:close', this.#handleExternalOverlayClose, {
      signal: this.#abortController.signal,
    });
    document.addEventListener('close', this.#handleExternalOverlayClose, { capture: true, signal: this.#abortController.signal });
    document.addEventListener('theme-drawer:open', this.#handleExternalOverlayOpen, {
      signal: this.#abortController.signal,
    });
    if (this.dataset.designMode === 'true') {
      const { signal } = this.#abortController;
      document.addEventListener('shopify:section:select', this.#handleEditorSelect, { signal });
      document.addEventListener('shopify:block:select', this.#handleEditorSelect, { signal });
      document.addEventListener('shopify:section:deselect', this.#handleEditorDeselect, { signal });
    }

    this.#mutationObserver = new MutationObserver(this.#handlePayloadMutation);
    this.#mutationObserver.observe(this.#payload, { childList: true, subtree: true });
    this.#handlePayloadMutation();

    this.#resolveConsent();
    if (this.dataset.designMode === 'true' && this.dataset.previewSideTab === 'true') this.#sideTabWrap?.removeAttribute('hidden');
    if (this.dataset.customerAcceptsMarketing === 'true' && !this.#feedbackTarget) {
      this.hidden = true;
      return;
    }
    if (this.dataset.enabled !== 'true') return;

    window.addEventListener('scroll', this.#handleScroll, {
      passive: true,
      signal: this.#abortController.signal,
    });
    this.#handleScroll();
    this.#armTimers();
    this.#ensurePrivacyApi();
  }

  disconnectedCallback() {
    this.#connectionVersion += 1;
    this.#pauseVisibleClock();
    this.#pendingOpen = null;
    this.#sideTabReturnFocus = null;
    this.#opened = false;
    this.#restorePayload();
    this.#abortController?.abort();
    this.#mutationObserver?.disconnect();
    clearTimeout(this.#autoTimer);
    clearTimeout(this.#sideTimer);
    this.#autoTimer = undefined;
    this.#sideTimer = undefined;
  }

  async openFrom(trigger) {
    if (!this.isConnected || this.dataset.enabled !== 'true' || this.dataset.customerAcceptsMarketing === 'true') return;
    return this.#openDrawer({ manual: true, trigger });
  }

  #handleEditorSelect = (event) => {
    if (String(event.detail?.sectionId) !== this.dataset.sectionId) return;
    void this.#openDrawer({ manual: true });
  };

  #handleEditorDeselect = (event) => {
    if (String(event.detail?.sectionId) !== this.dataset.sectionId) return;
    this.#drawer?.close?.();
    this.#hideSideTab();
  };

  #handleClick = (event) => {
    const dismiss = event.target instanceof Element ? event.target.closest('[data-tfp-newsletter-dismiss]') : null;
    if (dismiss instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      this.#writeSuppression();
      this.#writeSuppression('tab');
      this.#hideSideTab();
      return;
    }
    const trigger = event.target instanceof Element ? event.target.closest('[data-tfp-newsletter-open]') : null;
    if (!(trigger instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();
    void this.#openDrawer({ manual: true, trigger });
  };

  #handleDrawerClose = () => {
    this.#opened = false;
    this.#writeSuppression();
    this.#hideSideTab();
    this.#showSideTabIfEligible();
  };

  #handleExternalOverlayOpen = (event) => {
    if (event.target !== this.#drawer) {
      this.#pauseVisibleClock();
      this.#clearTimers();
      this.#hideSideTab();
    }
  };

  #handleConsentEvent = () => {
    this.#resolveConsent();
    if (this.#consentState === 'allowed') this.#armTimers();
    else {
      this.#pauseVisibleClock();
      this.#clearTimers();
      this.#hideSideTab();
    }
  };

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.#armTimers();
    } else {
      this.#pauseVisibleClock();
      this.#clearTimers();
    }
  };

  #handleExternalOverlayClose = (event) => {
    if (event.target === this.#drawer) return;
    this.#presentFeedback();
    this.#armTimers();
    this.#maybePresent();
  };

  #handleScroll = () => {
    this.#resolveConsent();
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const progress = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 100;
    if (progress >= Number(this.dataset.autoScrollPercent || 50)) this.#autoScrolled = true;
    if (progress >= Number(this.dataset.sideScrollPercent || 25)) this.#sideScrolled = true;
    this.#armTimers();
    this.#maybePresent();
  };

  #handlePayloadMutation = () => {
    const success = this.#payload?.querySelector('.email-signup__form .email-signup__message[role="status"]');
    const error = this.#payload?.querySelector('.email-signup__form .email-signup__message[role="alert"]');
    if (success) {
      this.#markConverted();
    }
    const feedback = error || success;
    const form = this.#payload?.querySelector('.email-signup__form');
    const ownsResponse = Boolean(form?.id) && window.location?.hash === `#${form.id}`;
    if (feedback && ownsResponse && !this.#feedbackPresented) {
      this.#feedbackTarget = feedback;
      queueMicrotask(() => this.#presentFeedback());
    }
  };

  async #presentFeedback() {
    if (
      !this.isConnected ||
      !this.#feedbackTarget?.isConnected ||
      this.#feedbackPresented ||
      this.#feedbackPresentationPending ||
      this.#isBlocked()
    ) return;
    this.#feedbackPresentationPending = true;
    try {
      await this.#openDrawer({ manual: true });
      if (!this.isConnected || !this.#opened || !this.#feedbackTarget?.isConnected) return;
      this.#feedbackPresented = true;
      if (!this.#feedbackTarget.hasAttribute('tabindex')) this.#feedbackTarget.setAttribute('tabindex', '-1');
      this.#feedbackTarget.focus({ preventScroll: true });
    } finally {
      this.#feedbackPresentationPending = false;
    }
  }

  #armTimers() {
    if (this.dataset.designMode === 'true') return;
    if (this.#consentState !== 'allowed' || document.visibilityState !== 'visible') return;
    if ((this.#hasSuppression() && this.#hasSuppression('tab')) || this.#isBlocked()) return;
    if (!this.#visibleSince) this.#visibleSince = Date.now();

    if (
    this.dataset.autoPopup === 'true' &&
      !this.#hasSuppression() &&
      this.dataset.pageType === 'index' &&
      !this.#autoTimer && !this.#autoDelayMet
    ) {
      const remaining = Math.max(0, Number(this.dataset.autoDelaySeconds || 20) * 1000 - this.#autoVisibleMs);
      this.#autoTimer = window.setTimeout(() => {
        this.#pauseVisibleClock();
        this.#visibleSince = Date.now();
        this.#autoTimer = undefined;
        this.#autoDelayMet = true;
        this.#maybePresent(true);
      }, remaining);
    }
    if (this.dataset.showSideTab === 'true' && !this.#hasSuppression('tab') && !this.#sideTimer && !this.#sideDelayMet && !['product', 'cart'].includes(this.dataset.pageType)) {
      const remaining = Math.max(0, Number(this.dataset.sideDelaySeconds || 20) * 1000 - this.#sideVisibleMs);
      this.#sideTimer = window.setTimeout(() => {
        this.#pauseVisibleClock();
        this.#visibleSince = Date.now();
        this.#sideTimer = undefined;
        this.#sideDelayMet = true;
        this.#maybePresent();
      }, remaining);
    }
  }

  #clearTimers() {
    clearTimeout(this.#autoTimer);
    clearTimeout(this.#sideTimer);
    this.#autoTimer = undefined;
    this.#sideTimer = undefined;
  }

  #pauseVisibleClock() {
    if (!this.#visibleSince) return;
    const elapsed = Math.max(0, Date.now() - this.#visibleSince);
    this.#autoVisibleMs += elapsed;
    this.#sideVisibleMs += elapsed;
    this.#visibleSince = 0;
  }

  #maybePresent(auto = false) {
    if (document.visibilityState !== 'visible' || this.#pendingOpen || this.#consentState !== 'allowed' || this.#isBlocked() || this.#opened) return;
    if (!this.#hasSuppression() && this.dataset.autoPopup === 'true' && this.#autoDelayMet && this.#autoScrolled) void this.#openDrawer();
    if (!this.#hasSuppression('tab') && this.dataset.showSideTab === 'true' && !['product', 'cart'].includes(this.dataset.pageType) && this.#sideDelayMet && this.#sideScrolled && this.#sideTab) {
      this.#sideTabWrap?.removeAttribute('hidden');
    }
  }

  async #openDrawer({ manual = false, trigger = null } = {}) {
    if (this.#pendingOpen) return this.#pendingOpen;
    if (!manual && (this.#consentState !== 'allowed' || this.#hasSuppression())) return;
    if (this.#isBlocked()) return;
    const connectionVersion = this.#connectionVersion;
    this.#sideTabReturnFocus = trigger === this.#sideTab ? this.#sideTab : null;
    this.#pendingOpen = (async () => {
      try {
      await import('@theme/theme-drawer');
      await customElements.whenDefined('theme-drawer');
      if (!this.isConnected || connectionVersion !== this.#connectionVersion) return;
      if (this.#isBlocked()) return;
      if (!manual && (document.visibilityState !== 'visible' || this.#consentState !== 'allowed' || this.#hasSuppression())) return;
      if (typeof this.#drawer.open !== 'function') throw new Error('Drawer unavailable');
      this.#movePayloadToDrawer();
      this.#opened = true;
      this.#hideSideTab();
      await this.#drawer.open({ returnFocus: trigger || null });
      } catch {
        if (!this.isConnected || connectionVersion !== this.#connectionVersion) return;
        this.#sideTabReturnFocus = null;
        this.#opened = false;
        this.#restorePayload();
        const footerEmail = [...document.querySelectorAll('.email-signup__form input[type="email"]')]
          .find((input) => !input.closest('tfp-newsletter-popup') && input.getClientRects().length);
        footerEmail?.scrollIntoView({ block: 'center', behavior: 'auto' });
        footerEmail?.focus({ preventScroll: true });
        this.#showSideTabIfEligible();
      } finally {
        if (connectionVersion === this.#connectionVersion) this.#pendingOpen = null;
      }
    })();
    return this.#pendingOpen;
  }

  #movePayloadToDrawer() {
    if (this.#payload.parentElement !== this.#slot) {
      this.#slot.append(this.#payload);
    }
  }

  #restorePayload() {
    if (this.#payload.parentElement === this.#slot) {
      this.#payloadHost?.append(this.#payload);
    }
  }

  #resolveConsent() {
    if (this.dataset.customerAcceptsMarketing === 'true') {
      this.#consentState = 'subscriber';
      return;
    }
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy || typeof privacy.marketingAllowed !== 'function') {
      this.#consentState = 'unknown';
      return;
    }
    try {
      const allowed = privacy.marketingAllowed();
      this.#consentState = allowed === true ? 'allowed' : allowed === false ? 'denied' : 'unknown';
    } catch {
      this.#consentState = 'unknown';
    }
  }

  #ensurePrivacyApi() {
    if (
      this.dataset.enabled !== 'true' ||
      this.dataset.customerAcceptsMarketing === 'true' ||
      this.dataset.designMode === 'true'
    ) return;

    if (window.Shopify?.customerPrivacy) {
      this.#handleConsentEvent();
      if (this.#consentState === 'allowed') {
        this.#maybePresent();
        this.#showSideTabIfEligible();
      }
      return;
    }

    if (this.#privacyLoadPending) return;

    if (typeof window.Shopify?.loadFeatures !== 'function') {
      this.#armPrivacyReadiness();
      return;
    }

    const connectionVersion = this.#connectionVersion;
    this.#privacyLoadPending = true;
    try {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error) => {
          if (!this.isConnected || connectionVersion !== this.#connectionVersion) return;
          this.#privacyLoadPending = false;
          if (error) {
            this.#handlePrivacyUnavailable();
            return;
          }
          this.#resolveConsent();
          if (this.#consentState !== 'allowed') {
            this.#handlePrivacyUnavailable();
            return;
          }
          this.#armTimers();
          this.#maybePresent();
          this.#showSideTabIfEligible();
        },
      );
    } catch {
      this.#privacyLoadPending = false;
      this.#handlePrivacyUnavailable();
    }
  }

  #armPrivacyReadiness() {
    if (this.#privacyReadinessArmed) return;
    this.#privacyReadinessArmed = true;
    const { signal } = this.#abortController;
    const retry = () => this.#ensurePrivacyApi();
    document.addEventListener('DOMContentLoaded', retry, { once: true, signal });
    window.addEventListener('load', retry, { once: true, signal });
  }

  #handlePrivacyUnavailable() {
    this.#resolveConsent();
    this.#pauseVisibleClock();
    this.#clearTimers();
    this.#hideSideTab();
  }

  #isBlocked() {
    if (document.querySelector('theme-drawer[open], dialog[open]')) return true;
    return Array.from(document.querySelectorAll('[aria-modal="true"]')).some((element) => {
      const style = getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
    });
  }

  #storageKey(kind = 'popup') {
    return `${STORAGE_PREFIX}${this.dataset.campaignKey || 'newsletter-v1'}${kind === 'tab' ? ':tab' : ''}`;
  }

  #hasSuppression(kind = 'popup') {
    if (kind === 'tab' ? this.#tabSuppressedInMemory : this.#suppressedInMemory) return true;
    if (!this.#preferencesProcessingAllowed()) return true;
    try {
      if (kind === 'tab' && localStorage.getItem(`${this.#storageKey()}:policy`) !== 'split-v2' && sessionStorage.getItem(`${this.#storageKey()}:policy`) !== 'split-v2') {
        const legacy = localStorage.getItem(this.#storageKey()) || sessionStorage.getItem(this.#storageKey());
        if (legacy !== null && Number(legacy) > Date.now()) return true;
      }
      const value = localStorage.getItem(this.#storageKey(kind));
      if (value !== null && Number(value) > Date.now()) return true;
      const sessionValue = sessionStorage.getItem(this.#storageKey(kind));
      return sessionValue !== null && Number(sessionValue) > Date.now();
    } catch {
      return true;
    }
  }

  #writeSuppression(kind = 'popup') {
    if (this.dataset.designMode === 'true') return;
    if (kind === 'tab') this.#tabSuppressedInMemory = true;
    else this.#suppressedInMemory = true;
    if (!this.#preferencesProcessingAllowed()) return;
    const expiry = Date.now() + Number(this.dataset.suppressionDays || 30) * 86400000;
    try {
      localStorage.setItem(`${this.#storageKey()}:policy`, 'split-v2');
      localStorage.setItem(this.#storageKey(kind), String(expiry));
      return;
    } catch {
    }
    try {
      sessionStorage.setItem(`${this.#storageKey()}:policy`, 'split-v2');
      sessionStorage.setItem(this.#storageKey(kind), String(expiry));
    } catch {
      // In-memory suppression remains the safe final fallback.
    }
  }

  #markConverted() {
    this.#writeSuppression();
    this.#writeSuppression('tab');
    this.#hideSideTab();
  }

  #hideSideTab() {
    this.#sideTabWrap?.setAttribute('hidden', '');
  }

  #showSideTabIfEligible() {
    if (this.#sideTabIsEligible()) {
      this.#sideTabWrap?.removeAttribute('hidden');
    }
  }

  #sideTabIsEligible() {
    if (
      this.#opened ||
      this.dataset.enabled !== 'true' ||
      this.dataset.showSideTab !== 'true' ||
      this.dataset.customerAcceptsMarketing === 'true' ||
      document.visibilityState !== 'visible' ||
      this.#isBlocked()
    ) return false;
    if (this.dataset.designMode === 'true' && this.dataset.previewSideTab === 'true') return true;
    return !['product', 'cart'].includes(this.dataset.pageType) &&
      this.#sideDelayMet &&
      this.#sideScrolled &&
      this.#consentState === 'allowed' &&
      !this.#hasSuppression('tab');
  }

  #restoreSideTabFocus() {
    const trigger = this.#sideTabReturnFocus;
    this.#sideTabReturnFocus = null;
    if (
      trigger !== this.#sideTab ||
      !trigger?.isConnected ||
      !this.#sideTabIsEligible() ||
      this.#sideTabWrap?.hasAttribute('hidden') ||
      trigger.hidden ||
      trigger.getClientRects().length === 0
    ) return;
    trigger.focus();
  }

  #preferencesProcessingAllowed() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return typeof privacy?.preferencesProcessingAllowed === 'function' && privacy.preferencesProcessingAllowed() === true;
    } catch { return false; }
  }
}

if (!customElements.get(TAG_NAME)) customElements.define(TAG_NAME, TfpNewsletterPopup);
